<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Experiences Management</h1>
        <span class="page-subtitle">{{ itemCount ? itemCount + ' experiences' : 'Manage all experiences' }}</span>
      </div>
      <div class="page-header__right">
        <button class="btn-secondary-action" @click="showAddBlackoutDateModal">
          <i class="fa fa-calendar-times-o"></i> Add Blackout Date
        </button>
        <button class="btn-secondary-action" @click="showAddTagModal">
          <i class="fa fa-tag"></i> Add Tag
        </button>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">

        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search by Name</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search experiences..."
                v-model="inputFilters.search"
                class="dd-input dd-input--icon"
            />
          </div>
        </div>

        <div class="filter-field filter-field--md">
          <label class="filter-label">Category</label>
          <select v-model="inputFilters.category" class="dd-input">
            <option value="">All Categories</option>
            <option
                v-for="cat in categories"
                :key="cat.id"
                :value="cat.id"
            >{{ cat.title }}</option>
          </select>
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Categories</th>
            <th>Tags</th>
            <th>Blackout Dates</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="product in items" :key="product.id" class="dd-table__row">
            <td><span class="order-id">#{{ product.id }}</span></td>
            <td><span class="exp-title">{{ product.title }}</span></td>
            <td>
              <span class="text-muted" v-if="getCategories(product) === '—'">—</span>
              <span class="cell-text" :title="getCategories(product)" v-else>
                  {{ short(getCategories(product), 45) }}
                </span>
            </td>
            <td>
                <span
                    :class="['tags-cell', getTags(product) === 'Add' ? 'tags-cell--add' : 'tags-cell--set']"
                    :title="getTags(product) !== 'Add' ? getTags(product) : ''"
                    @click="showEditTagsModal($event, product.id)"
                >
                  <i v-if="getTags(product) === 'Add'" class="fa fa-plus-circle"></i>
                  <i v-else class="fa fa-tag"></i>
                  {{ getTags(product) === 'Add' ? 'Add tags' : short(getTags(product), 40) }}
                </span>
            </td>
            <td>
                <span
                    :class="['tags-cell', getBlackoutDates(product) === 'Add' ? 'tags-cell--add' : 'tags-cell--set']"
                    :title="getBlackoutDates(product) !== 'Add' ? getBlackoutDates(product) : ''"
                    @click="showEditBlackoutDatesModal($event, product)"
                >
                  <i v-if="getBlackoutDates(product) === 'Add'" class="fa fa-plus-circle"></i>
                  <i v-else class="fa fa-calendar-times-o"></i>
                  {{ getBlackoutDates(product) === 'Add' ? 'Add dates' : short(getBlackoutDates(product), 40) }}
                </span>
            </td>
            <td @click.prevent>
              <b-dropdown variant="none" no-caret boundary="viewport" right>
                <template #button-content>
                  <div class="action-menu-btn">
                    <i class="fa fa-ellipsis-h"></i>
                  </div>
                </template>
                <b-dropdown-item @click="showBuyForForm(product.id)">
                  <i class="fa fa-shopping-cart mr-2"></i> Buy it for...
                </b-dropdown-item>
                <b-dropdown-item @click="showEditMetaDataModal(product)">
                  <i class="fa fa-code mr-2"></i> Edit Meta Data
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>

          <tr v-if="items && items.length === 0">
            <td colspan="6" class="empty-state">
              <i class="fa fa-compass"></i>
              <div>No experiences found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">{{ itemCount }} total experiences</div>
        <b-pagination
            v-model="page"
            :total-rows="itemCount"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

    <!-- Modals (unchanged) -->
    <modal name="add-tag-modal" width="75%" height="auto" :adaptive="true">
      <add-tag-modal :categories="categories" :tags="tags" v-on:done="loadItems()"></add-tag-modal>
    </modal>

    <modal name="buy-for-modal" height="auto">
      <buy-for-modal :experienceId="buyForFormExperienceId"></buy-for-modal>
    </modal>

    <modal name="edit-tags-modal" height="auto">
      <edit-tags-modal
          :tags="tags"
          :etOldTags="etOldTags"
          :etSelectedTags="etSelectedTags"
          :etSelectedExperience="etSelectedExperience"
          v-on:done="loadItems()"
      ></edit-tags-modal>
    </modal>

    <modal name="add-blackout-date-modal" width="75%" height="auto" :adaptive="true">
      <add-blackout-date-modal :blackoutDates="blackoutDates" :categories="categories" v-on:done="loadItems()"></add-blackout-date-modal>
    </modal>

    <modal name="edit-blackout-dates-modal" width="75%" height="auto" :adaptive="true">
      <edit-blackout-dates-modal
          :blackoutDates="blackoutDates"
          :productId="ebProductId"
          :blackoutDateIds="ebBlackoutDateIds"
          v-on:done="$modal.hide('edit-blackout-dates-modal'); loadItems();"
      ></edit-blackout-dates-modal>
    </modal>

    <modal name="edit-meta-data-modal" height="auto">
      <edit-meta-data-modal :experience="editMetaDataExperience"></edit-meta-data-modal>
    </modal>

  </div>
</template>

<script>
import { config, short } from "../helpers";
import AddBlackoutDateModal from "../components/ViewComponents/Experiences/AddBlackoutDateModal.vue";
import EditBlackoutDatesModal from "../components/ViewComponents/Experiences/EditBlackoutDatesModal.vue";
import AddTagModal from "../components/ViewComponents/Experiences/AddTagModal.vue";
import BuyForModal from "../components/ViewComponents/Experiences/BuyForModal.vue";
import EditTagsModal from "../components/ViewComponents/Experiences/EditTagsModal.vue";
import EditMetaDataModal from "../components/ViewComponents/Experiences/EditMetaDataModal.vue";

export default {
  name: "Experiences",
  components: {
    "add-blackout-date-modal":    AddBlackoutDateModal,
    "edit-blackout-dates-modal":  EditBlackoutDatesModal,
    "add-tag-modal":              AddTagModal,
    "buy-for-modal":              BuyForModal,
    "edit-tags-modal":            EditTagsModal,
    "edit-meta-data-modal":       EditMetaDataModal,
  },
  data() {
    return {
      url: "",
      items: [],
      tags: [],
      categories: [],
      blackoutDates: [],
      inputFilters: { search: null, category: "" },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      perPage: 1,
      itemCount: null,
      buyForFormExperienceId: null,
      etSelectedExperience: null,
      etOldTags: [],
      etSelectedTags: [],
      ebProductId: null,
      ebBlackoutDateIds: [],
      editMetaDataExperience: null,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/products";
    this.loadItems();
    this.loadTags();
    this.loadCategories();
    this.loadBlackoutDates();
  },

  methods: {
    loadItems() {
      let myData = { page: this.page };
      if (this.isFiltered) myData = { ...myData, filters: this.filters };
      axios.get(this.url, { params: myData }).then((r) => {
        this.items     = r.data.data;
        this.page      = r.data.meta.current_page;
        this.lastPage  = r.data.meta.last_page;
        this.itemCount = r.data.meta.total;
        this.perPage   = r.data.meta.per_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      const filters = {};
      if (this.inputFilters.search)   filters.title    = this.inputFilters.search;
      if (this.inputFilters.category) filters.category = { ids: this.inputFilters.category };
      this.filters = filters;
      this.loadItems();
    },

    loadTags()         { axios.get(config("BACKEND_ROOT") + "/admin/tags").then((r) => { this.tags = r.data.data; }); },
    loadCategories()   { axios.get(config("BACKEND_ROOT") + "/categories").then((r) => { this.categories = r.data.data; }); },
    loadBlackoutDates(){ axios.get(config("BACKEND_ROOT") + "/admin/blackoutdates").then((r) => { this.blackoutDates = r.data.data; }); },

    getCategories(product) {
      const cats = product.categories.map((c) => c.title);
      return cats.length ? cats.join(", ") : "—";
    },

    getTags(product) {
      const tags = product.tags.filter((t) => t.key.startsWith("dd_tag_"));
      const names = [];
      for (const tag of tags) {
        const sys = this.tags.find((s) => tag.key.split("_", 3)[2] == s.id);
        if (sys) names.push(sys.title);
      }
      return names.length ? names.join(", ") : "Add";
    },

    getBlackoutDates(product) {
      return product.blackout_dates.length
          ? product.blackout_dates.map((o) => o.title).join(", ")
          : "Add";
    },

    showAddTagModal()          { this.$modal.show("add-tag-modal"); },
    showAddBlackoutDateModal() { this.$modal.show("add-blackout-date-modal"); },

    showBuyForForm(productId) {
      this.buyForFormExperienceId = productId;
      this.$modal.show("buy-for-modal");
    },

    showEditTagsModal(e, productId) {
      const titles = e.target.closest("[title]")?.getAttribute("title")?.split(", ") ?? [];
      this.etOldTags        = this.tags.filter((t) => titles.includes(t.title)).map((t) => t.id);
      this.etSelectedTags   = [...this.etOldTags];
      this.etSelectedExperience = productId;
      this.$modal.show("edit-tags-modal");
    },

    showEditBlackoutDatesModal(e, product) {
      this.ebProductId       = product.id;
      this.ebBlackoutDateIds = product.blackout_dates.map((o) => o.id);
      this.$modal.show("edit-blackout-dates-modal");
    },

    showEditMetaDataModal(product) {
      this.editMetaDataExperience = product;
      this.$modal.show("edit-meta-data-modal");
    },

    short(string, limit) { return short(string, limit); },
  },

  watch: {
    page() { this.loadItems(); },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap   { display: flex; flex-direction: column; gap: 16px; }
.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.btn-secondary-action { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-secondary-action:hover { border-color: var(--accent); color: var(--accent); }

/* ── Filter ── */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--md     { min-width: 180px; max-width: 240px; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* ── Table ── */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.order-id  { font-weight: 500; color: var(--text-1); }
.exp-title { font-weight: 500; color: var(--text-1); }
.cell-text { font-size: 12px; color: var(--text-2); cursor: default; }
.text-muted { color: var(--text-3); font-size: 12px; }

/* Tags / Blackout clickable cells */
.tags-cell { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; cursor: pointer; transition: background .15s, color .15s; white-space: nowrap; max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
.tags-cell--set  { background: rgba(129,140,248,.1); color: #818cf8; border: 1px solid rgba(129,140,248,.2); }
.tags-cell--set:hover  { background: rgba(129,140,248,.2); }
.tags-cell--add  { background: var(--bg-hover); color: var(--text-3); border: 1px solid var(--border); }
.tags-cell--add:hover  { border-color: var(--accent); color: var(--accent); background: rgba(244,47,75,.06); }

/* Action menu */
.action-menu-btn { width: 30px; height: 30px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-2); font-size: 14px; transition: border-color .15s, color .15s; }
.action-menu-btn:hover { border-color: var(--accent); color: var(--accent); }
.dd-table__row td:last-child .btn { padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }

.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.dd-pagination >>> .page-link { background: var(--bg-hover); border-color: var(--border); color: var(--text-2); font-size: 13px; }
.dd-pagination >>> .page-item.active .page-link { background: var(--accent); border-color: var(--accent); color: #fff; }
.dd-pagination >>> .page-link:hover { background: var(--bg-hover); border-color: var(--accent); color: var(--accent); }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--md, .filter-field--grow { width: 100%; max-width: 100%; }
  .page-header__right { width: 100%; }
  .btn-secondary-action { flex: 1; justify-content: center; }
}
</style>